import React, { useState, useEffect } from "react";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import "./StackedColumnChart.scss";
import { formatNumber } from "../../../utils/formatNumber";

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f2f2f4",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
  },
}));

const StackedColumnChart = (props: any) => {
  const { data, height = 200, decimalPrecision = 2, measurementUnit } = props;
  const [items, setItems] = useState(data as any);
  const [xAxisItems, setXAxisItems] = useState([] as any);
  const [yAxisItems, setYAxisItems] = useState([] as any);
  const [legendItems, setLegendItems] = useState([] as any);
  const [filteredItems, setFilteredItems] = useState(items);

  const addHeightToStackedItems = (items: any, height: number) => {
    const maxTotalValue = Math.max(
      ...items.map((item: any) =>
        item.data.reduce((sum: number, subItem: any) => sum + subItem.value, 0)
      )
    );

    return items.map((item: any) => {
      const periodTotal = item.data.reduce(
        (sum: number, subItem: any) => sum + subItem.value,
        0
      );
      const periodHeight = (periodTotal / maxTotalValue) * height;

      let accumulatedHeight = 0;

      const dataWithHeight = item.data.map((subItem: any) => {
        const subItemHeight = (subItem.value / periodTotal) * periodHeight;
        accumulatedHeight += subItemHeight;
        return {
          ...subItem,
          height: Math.round(subItemHeight),
        };
      });

      return {
        ...item,
        data: dataWithHeight,
        totalHeight: Math.round(periodHeight),
      };
    });
  };

  function getXAxisItems(n: number) {
    if (n < 2 || n > items.length) {
      return [];
    }

    const result = [];
    const step = (items.length - 1) / (n - 1);

    for (let i = 0; i < n; i++) {
      const index = Math.round(i * step);
      result.push(items[index].display_period);
    }

    return result;
  }

  function getYAxisItems(n: number) {
    if (n < 2) {
      return [];
    }
    const selectedCount = legendItems.filter((item: any) => item.selected).length;
    if (selectedCount == 0) {
      return [];
    }

    const periodSums = filteredItems.map((item: any) =>
      item.data.reduce((sum: number, subItem: any) => sum + subItem.value, 0)
    );

    const maxValue = Math.max(...periodSums);
    if (maxValue === 0) {
      return [];
    }

    const interval = maxValue / (n - 1);
    const result = [];

    for (let i = 0; i < n; i++) {
      result.push(Math.round(i * interval * 100) / 100);
    }

    result.sort((a, b) => b - a);

    return result;
  }

  function getLegendItems() {
    const legendItems: any = [];

    items.forEach((item: any) => {
      item.data.forEach((subItem: any) => {
        if (!legendItems.some((entry: any) => entry.label === subItem.label)) {
          legendItems.push({
            label: subItem.label,
            color: subItem.color || "#ccc",
            selected: true,
          });
        }
      });
    });

    return legendItems;
  }

  useEffect(() => {
    if (data) {
      setItems([]);
      const n = 8;
      const newData = data.length > n ? data.slice(-n) : data;
      const columns = addHeightToStackedItems(newData, height);
      setTimeout(() => {
        setItems(columns);
      }, 100);
    }
  }, [data]);

  function isSelected(label: string, legendItems: any) {
    const item = legendItems.find((item: any) => item.label === label);
    return item ? item.selected : false;
  }

  useEffect(() => {
    if (items && legendItems && legendItems.length > 0) {
      const maxValue = items.reduce((max: number, item: any) => {
        return Math.max(
          max,
          ...item.data
            .filter((subItem: any) => isSelected(subItem.label, legendItems))
            .map((subItem: any) => subItem.value)
        );
      }, 0);

      const newFilteredItems = items.map((item: any) => {
        return {
          ...item,
          data: item.data
            .filter((subItem: any) => isSelected(subItem.label, legendItems))
            .map((subItem: any) => {
              return {
                ...subItem,
                //height: Math.round(adjustHeight(subItem.value, maxValue)),
              };
            }),
        };
      });
      const columns = addHeightToStackedItems(newFilteredItems, height);
      setFilteredItems(columns);
    }
  }, [legendItems]);

  useEffect(() => {
    if (items && items.length > 0) {
      setXAxisItems(() => getXAxisItems(items.length > 6 ? 6 : items.length));
      setLegendItems(() => getLegendItems());
    }
  }, [items]);

  useEffect(() => {
    if (items && filteredItems && filteredItems.length > 0)
      setYAxisItems(() => getYAxisItems(5));
  }, [filteredItems]);

  function handleLegendItemClick(index: number) {
    setLegendItems((prev: any) => {
      const prevCopy: any = [...prev];
      prevCopy[index].selected = !prevCopy[index].selected;
      return prevCopy;
    });
  }

  return (
    <>
      <div className="stacked-column-chart-container">
        <div className="chart">
          <div>
            <div className="y-axis" style={{ height: height + "px" }}>
              {yAxisItems.map((item: any, index: number) => {
                return <span key={index}>{formatNumber(item, decimalPrecision)}</span>;
              })}
            </div>
            <div className="main-content" style={{ height: height + "px" }}>
              {filteredItems?.map((item: any, index: number) => {
                return (
                  <div
                    key={index}
                    className="columns-wrapper"
                    style={{
                      height: height + "px",
                    }}
                  >
                    {item.data.map((subItem: any, subIndex: number) => {
                      return (
                        <HtmlTooltip
                          key={subIndex}
                          title={
                            <React.Fragment>
                              <div className="sub-column-tooltip">
                                <strong>{item.display_period}</strong>
                                <span style={{ color: subItem.color }}>{subItem.label}</span>
                                <div>
                                  <strong style={{ color: subItem.color }}>
                                    {formatNumber(subItem.value, decimalPrecision)}
                                  </strong>
                                  <span>({measurementUnit})</span>
                                </div>
                              </div>
                            </React.Fragment>
                          }
                        >
                          <span
                            key={subIndex}
                            style={{
                              height: subItem.height + "px",
                              background: subItem.color || "#ccc",
                            }}
                          ></span>
                        </HtmlTooltip>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="x-axis">
            {xAxisItems.map((item: any, index: number) => {
              return <span key={index}>{item}</span>;
            })}
          </div>
        </div>
        <div className="legend">
          {legendItems.map((item: any, index: number) => {
            return (
              <a
                className="legend-item"
                key={index}
                onClick={() => {
                  handleLegendItemClick(index);
                }}
              >
                <span
                  className="legend-color"
                  style={{
                    backgroundColor: item.selected ? item.color : "#ccc",
                  }}
                ></span>
                <span className="legend-label">{item.label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </>
  );
};

export { StackedColumnChart };
